import type { ReactNode } from 'react'
import { Box, Stack } from '@mui/material'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'
import { CARD_COLORS } from '@ds/theme/color-mode'
import { PublisherRequestTypeEnum } from '@modules/api-core'
import { SongsListHeader } from './SongsListHeader'
import { SONGS_LIST_STRINGS } from './strings'
import { PublisherApprovalRequest } from './approval/PublisherApprovalRequest'
import { PublisherCommonApprovalRequest } from './approval-common/PublisherCommonApprovalRequest'
import { PublisherApprovalLyricsRequest } from './approval-lyrics/PublisherApprovalLyricsRequest'
import { PublisherCopyrightRequest } from './copyright/PublisherCopyrightRequest'
import type { TPublisherRequestShowProps } from './types'

export const PublisherRequest = ({
  requestId,
  selectedRequest,
  detailsOpen,
  chatOpen,
  onShowDetails,
  onShowChat,
  onShare,
  onClose,
  initialLoading,
  enums,
  apiUrl,
  onDrawerClose,
  onReviewSuccess,
  isRequestSubmitted,
}: TPublisherRequestShowProps) => {
  const withdrawn = Boolean(selectedRequest.withdrawnAt)
  const sharedRequestProps = {
    requestId,
    selectedRequest,
    initialLoading,
    enums,
    apiUrl,
    onDrawerClose,
    onReviewSuccess,
    isRequestSubmitted: isRequestSubmitted || withdrawn,
  }

  let requestBody: ReactNode
  if (selectedRequest.type === PublisherRequestTypeEnum.COPYRIGHT_REQUEST) {
    requestBody = <PublisherCopyrightRequest {...sharedRequestProps} />
  } else if (selectedRequest.type === PublisherRequestTypeEnum.LYRICS_APPROVAL_REQUEST) {
    requestBody = <PublisherApprovalLyricsRequest {...sharedRequestProps} />
  } else if (selectedRequest.type === PublisherRequestTypeEnum.APPROVAL_REQUEST) {
    requestBody = <PublisherApprovalRequest {...sharedRequestProps} />
  } else if (selectedRequest.type === PublisherRequestTypeEnum.COMMON_APPROVAL_REQUEST) {
    requestBody = <PublisherCommonApprovalRequest {...sharedRequestProps} />
  } else {
    throw new Error('Unknown request type')
  }

  return (
    <>
      <SongsListHeader
        songsCount={
          selectedRequest.songs?.length || selectedRequest.clientRequest?.songs?.length || 0
        }
        reviewType={
          selectedRequest.type === PublisherRequestTypeEnum.COPYRIGHT_REQUEST
            ? SONGS_LIST_STRINGS.copyright
            : SONGS_LIST_STRINGS.approval
        }
        detailsOpen={detailsOpen}
        chatOpen={chatOpen}
        onShowDetails={onShowDetails}
        onShowChat={onShowChat}
        onShare={withdrawn ? undefined : onShare}
        onClose={onClose}
      />
      {withdrawn && (
        <Stack
          role="status"
          direction="row"
          alignItems="flex-start"
          gap={1.5}
          sx={{
            mx: 4,
            mt: 2.75,
            px: 2.5,
            py: 2,
            fontSize: 15,
            bgcolor: CARD_COLORS.withdrawnBg,
            color: CARD_COLORS.withdrawnText,
          }}
        >
          <RemoveCircleOutlineIcon sx={{ fontSize: 20, mt: 0.125 }} />
          <span>
            <Box component="strong" sx={{ fontWeight: 600 }}>
              {SONGS_LIST_STRINGS.withdrawnTitle}
            </Box>
          </span>
        </Stack>
      )}
      {requestBody}
    </>
  )
}
